/** @module @liquidefy/code-editor/ai/prompts/context @version 1.0.0 @license LQD Research LLC */
/** @rust code_editor_ai_prompts_context.rs — false; 4/2/26; sm */

// ═══════════════════════════════════════════════════════════════════════════════
// CONTEXT ASSEMBLY — Gather, rank, and truncate code context for prompts.
// Builds the "what the model needs to know" payload. Pure functions only.
// ═══════════════════════════════════════════════════════════════════════════════

import type { AIRequest, AIRequestType, Position, Selection } from '../../code-editor-types.js';
import { estimateTokens } from './templates.js';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ContextItemType =
  | 'cursor-surrounding'
  | 'selection'
  | 'current-file'
  | 'imports'
  | 'symbols'
  | 'type-definitions'
  | 'related-files'
  | 'error-context'
  | 'history'
  | 'instructions'
  | 'examples'
  | 'project-structure';

export interface ContextItem {
  readonly type: ContextItemType;
  readonly content: string;
  readonly relevance: number;
  readonly tokens: number;
  readonly source: string;
  readonly priority: number;
}

export interface ContextWindow {
  readonly items: readonly ContextItem[];
  readonly totalTokens: number;
  readonly maxTokens: number;
  readonly utilization: number;
}

export interface ContextConfig {
  readonly maxTokens: number;
  readonly cursorWindowLines: number;
  readonly includeImports: boolean;
  readonly includeSymbols: boolean;
  readonly includeRelated: boolean;
  readonly includeHistory: boolean;
  readonly reserveForOutput: number;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: ContextConfig = {
  maxTokens: 4000,
  cursorWindowLines: 50,
  includeImports: true,
  includeSymbols: true,
  includeRelated: true,
  includeHistory: false,
  reserveForOutput: 1000,
};

export function createContextConfig(overrides?: Partial<ContextConfig>): ContextConfig {
  if (!overrides) return DEFAULT_CONFIG;
  return { ...DEFAULT_CONFIG, ...overrides };
}

// ─── Context window operations ───────────────────────────────────────────────

export function createContextWindow(maxTokens: number): ContextWindow {
  return { items: [], totalTokens: 0, maxTokens, utilization: 0 };
}

export function addContextItem(window: ContextWindow, item: ContextItem): ContextWindow {
  if (window.totalTokens + item.tokens > window.maxTokens) return window;
  const newTotal = window.totalTokens + item.tokens;
  return {
    items: [...window.items, item],
    totalTokens: newTotal,
    maxTokens: window.maxTokens,
    utilization: window.maxTokens > 0 ? newTotal / window.maxTokens : 0,
  };
}

export function removeContextItem(window: ContextWindow, index: number): ContextWindow {
  if (index < 0 || index >= window.items.length) return window;
  const removed = window.items[index]!;
  const newItems = [...window.items];
  newItems.splice(index, 1);
  const newTotal = window.totalTokens - removed.tokens;
  return {
    items: newItems,
    totalTokens: newTotal,
    maxTokens: window.maxTokens,
    utilization: window.maxTokens > 0 ? newTotal / window.maxTokens : 0,
  };
}

// ─── Context extraction functions ────────────────────────────────────────────

/** Extract N lines around the cursor position. */
export function extractCursorContext(
  content: string,
  cursorLine: number,
  windowLines: number,
): ContextItem {
  const lines = content.split('\n');
  const halfWindow = Math.floor(windowLines / 2);
  const start = Math.max(0, cursorLine - halfWindow);
  const end = Math.min(lines.length, cursorLine + halfWindow);
  const extracted = lines.slice(start, end).join('\n');
  const tokens = estimateTokens(extracted);
  return {
    type: 'cursor-surrounding',
    content: extracted,
    relevance: 1.0,
    tokens,
    source: `lines ${start + 1}-${end}`,
    priority: 10,
  };
}

/** Extract selected text as context. */
export function extractSelectionContext(content: string, selection: Selection): ContextItem {
  const lines = content.split('\n');
  const startLine = Math.max(0, Math.min(selection.anchor.line, selection.active.line));
  const endLine = Math.min(lines.length - 1, Math.max(selection.anchor.line, selection.active.line));

  const startCol = startLine === selection.anchor.line ? selection.anchor.column : selection.active.column;
  const endCol = endLine === selection.active.line ? selection.active.column : selection.anchor.column;

  let extracted: string;
  if (startLine === endLine) {
    const line = lines[startLine] ?? '';
    extracted = line.slice(Math.min(startCol, endCol), Math.max(startCol, endCol));
  } else {
    const result: string[] = [];
    result.push((lines[startLine] ?? '').slice(startCol));
    for (let i = startLine + 1; i < endLine; i++) {
      result.push(lines[i] ?? '');
    }
    result.push((lines[endLine] ?? '').slice(0, endCol));
    extracted = result.join('\n');
  }

  const tokens = estimateTokens(extracted);
  return {
    type: 'selection',
    content: extracted,
    relevance: 1.0,
    tokens,
    source: `selection ${startLine + 1}:${startCol}-${endLine + 1}:${endCol}`,
    priority: 9,
  };
}

/** Gather all import/require lines from file content. */
export function extractImportContext(content: string): ContextItem {
  const lines = content.split('\n');
  const importLines: string[] = [];
  let multi = false;
  for (const line of lines) {
    const t = line.trim();
    if (multi) {
      importLines.push(line);
      if (t.includes('}') || t.endsWith(';') || t.endsWith("'") || t.endsWith('"')) multi = false;
      continue;
    }
    const isImport = t.startsWith('import ') || t.startsWith('import{') || t.startsWith('from ') ||
      /^(const|let|var)\s+.*=\s*require\(/.test(t) || t.startsWith('export { ') || t.startsWith('export type {');
    if (isImport) {
      importLines.push(line);
      if (!t.endsWith(';') && !t.endsWith("'") && !t.endsWith('"')) multi = true;
    }
  }
  const extracted = importLines.join('\n');
  return { type: 'imports', content: extracted, relevance: 0.6, tokens: estimateTokens(extracted), source: 'imports', priority: 5 };
}

/** Extract function/class/type/interface declaration signatures (not bodies). */
export function extractSymbolContext(content: string): ContextItem {
  const lines = content.split('\n');
  const sigs: string[] = [];
  const PATS = [
    /^\s*(export\s+)?(async\s+)?function\s+\w+/, /^\s*(export\s+)?(const|let|var)\s+\w+\s*=\s*(async\s+)?\(/,
    /^\s*(export\s+)?(const|let|var)\s+\w+\s*=\s*(async\s+)?\w+\s*=>/, /^\s*(export\s+)?(abstract\s+)?class\s+\w+/,
    /^\s*(export\s+)?(type|interface)\s+\w+/, /^\s*(export\s+)?enum\s+\w+/,
  ];
  for (const line of lines) {
    for (const p of PATS) {
      if (p.test(line)) {
        const b = line.indexOf('{');
        sigs.push(b >= 0 ? line.slice(0, b + 1) + ' ... }' : line);
        break;
      }
    }
  }
  const extracted = sigs.join('\n');
  return { type: 'symbols', content: extracted, relevance: 0.7, tokens: estimateTokens(extracted), source: 'symbols', priority: 6 };
}

/** Format error diagnostics as context. */
export function extractErrorContext(diagnostics: readonly string[]): ContextItem {
  const extracted = diagnostics.join('\n');
  return { type: 'error-context', content: extracted, relevance: 0.9, tokens: estimateTokens(extracted), source: 'diagnostics', priority: 8 };
}

/** Generate a tree-like file listing from paths. */
export function extractProjectStructure(filePaths: readonly string[], maxDepth?: number): ContextItem {
  const tree = buildTreeString(filePaths, maxDepth ?? 3);
  return { type: 'project-structure', content: tree, relevance: 0.3, tokens: estimateTokens(tree), source: 'project', priority: 2 };
}

function buildTreeString(paths: readonly string[], maxDepth: number): string {
  type TN = { ch: Record<string, TN>; f: boolean };
  const root: TN = { ch: {}, f: false };
  for (const p of paths) {
    const parts = p.split('/').filter(Boolean);
    let n = root;
    for (let i = 0; i < Math.min(parts.length, maxDepth + 1); i++) {
      if (!n.ch[parts[i]!]) n.ch[parts[i]!] = { ch: {}, f: i === parts.length - 1 };
      n = n.ch[parts[i]!]!;
    }
  }
  const out: string[] = [];
  const walk = (node: TN, d: number): void => {
    for (const [name, child] of Object.entries(node.ch).sort(([a], [b]) => a.localeCompare(b))) {
      out.push('  '.repeat(d) + (child.f ? '' : '/') + name);
      if (!child.f && d < maxDepth) walk(child, d + 1);
    }
  };
  walk(root, 0);
  return out.join('\n');
}

// ─── Relevance ranking ──────────────────────────────────────────────────────

// Priority weights by request type — which context items matter most
// Keys are ContextItemType abbreviations mapped in rankByRelevance
const W: Record<string, Partial<Record<ContextItemType, number>>> = {
  completion:      { 'cursor-surrounding': 1.0, imports: 0.8, symbols: 0.7, selection: 0.5 },
  suggestion:      { 'cursor-surrounding': 0.9, symbols: 0.8, imports: 0.6, selection: 0.7 },
  analysis:        { 'current-file': 1.0, symbols: 0.8, imports: 0.6, 'error-context': 0.9 },
  generation:      { instructions: 1.0, imports: 0.7, symbols: 0.8, 'type-definitions': 0.9 },
  refactor:        { selection: 1.0, 'cursor-surrounding': 0.8, 'type-definitions': 0.9, imports: 0.6 },
  explain:         { selection: 1.0, 'cursor-surrounding': 0.9, imports: 0.5, symbols: 0.4 },
  fix:             { 'error-context': 1.0, 'cursor-surrounding': 0.9, imports: 0.7, symbols: 0.6 },
  'test-generate': { selection: 1.0, imports: 0.9, symbols: 0.8, 'type-definitions': 0.7 },
  document:        { selection: 1.0, symbols: 0.9, imports: 0.5, 'type-definitions': 0.7 },
  review:          { 'current-file': 1.0, symbols: 0.7, imports: 0.5, 'error-context': 0.6 },
  conversation:    { 'cursor-surrounding': 0.7, selection: 0.8, imports: 0.4, symbols: 0.5 },
  optimize:        { selection: 1.0, 'cursor-surrounding': 0.8, symbols: 0.6, imports: 0.5 },
  translate:       { selection: 1.0, imports: 0.7, 'type-definitions': 0.6, symbols: 0.5 },
};

/** Score each item's relevance to the request type. */
export function rankByRelevance(items: readonly ContextItem[], request: AIRequest): ContextItem[] {
  const weights = W[request.type] ?? {};
  const scored = items.map(item => {
    const typeWeight = weights[item.type] ?? 0.5;
    const adjustedRelevance = item.relevance * typeWeight;
    return { ...item, relevance: adjustedRelevance };
  });
  return scored.sort((a, b) => (b.relevance * b.priority) - (a.relevance * a.priority));
}

// ─── Packing & truncation ────────────────────────────────────────────────────

/** Truncate item content to fit maxTokens. Keep start and end, cut middle. */
export function truncateItem(item: ContextItem, maxTokens: number): ContextItem {
  if (item.tokens <= maxTokens) return item;
  const targetChars = Math.max(40, maxTokens * 4); // rough inverse of estimateTokens
  const half = Math.floor(targetChars / 2);
  const truncated = item.content.slice(0, half) + '\n... [truncated] ...\n' + item.content.slice(-half);
  return {
    ...item,
    content: truncated,
    tokens: estimateTokens(truncated),
  };
}

/** Combine items of the same type into a single item. */
export function mergeContextItems(items: readonly ContextItem[]): ContextItem {
  if (items.length === 0) {
    return { type: 'instructions', content: '', relevance: 0, tokens: 0, source: '', priority: 0 };
  }
  if (items.length === 1) return items[0]!;
  const combined = items.map(i => i.content).join('\n\n');
  const tokens = estimateTokens(combined);
  return {
    type: items[0]!.type,
    content: combined,
    relevance: Math.max(...items.map(i => i.relevance)),
    tokens,
    source: items.map(i => i.source).join(', '),
    priority: Math.max(...items.map(i => i.priority)),
  };
}

/**
 * Greedy packing: sort by (relevance x priority), add items until full.
 * Truncate the last item for partial fit.
 */
export function packContext(items: readonly ContextItem[], maxTokens: number): ContextWindow {
  const sorted = [...items].sort((a, b) => (b.relevance * b.priority) - (a.relevance * a.priority));
  const packed: ContextItem[] = [];
  let totalTokens = 0;

  for (const item of sorted) {
    if (totalTokens + item.tokens <= maxTokens) {
      packed.push(item);
      totalTokens += item.tokens;
    } else {
      const remaining = maxTokens - totalTokens;
      if (remaining > 20) {
        const truncated = truncateItem(item, remaining);
        packed.push(truncated);
        totalTokens += truncated.tokens;
      }
      break;
    }
  }

  return {
    items: packed,
    totalTokens,
    maxTokens,
    utilization: maxTokens > 0 ? totalTokens / maxTokens : 0,
  };
}

// ─── Formatting ─────────────────────────────────────────────────────────────

const SECTION_HEADERS: Record<ContextItemType, string> = {
  'cursor-surrounding': '### Surrounding Code',
  'selection':          '### Selected Code',
  'current-file':       '### Current File',
  'imports':            '### Imports',
  'symbols':            '### Visible Symbols',
  'type-definitions':   '### Type Definitions',
  'related-files':      '### Related Files',
  'error-context':      '### Errors & Diagnostics',
  'history':            '### Recent Edits',
  'instructions':       '### Instructions',
  'examples':           '### Examples',
  'project-structure':  '### Project Structure',
};

/** Format all items into a single string for prompt injection. */
export function contextToString(window: ContextWindow): string {
  return window.items.map(item => item.content).join('\n\n');
}

/** Format as named sections. */
export function contextToSections(window: ContextWindow): Record<string, string> {
  const sections: Record<string, string> = {};
  for (const item of window.items) {
    const header = SECTION_HEADERS[item.type] ?? `### ${item.type}`;
    if (sections[header]) {
      sections[header] += '\n\n' + item.content;
    } else {
      sections[header] = item.content;
    }
  }
  return sections;
}

/** How full is the window (0-1). */
export function contextUtilization(window: ContextWindow): number {
  return window.utilization;
}

/** Estimate how many tokens context assembly will consume. */
export function estimateContextTokens(
  content: string,
  cursorLine: number,
  config: ContextConfig,
): number {
  let total = 0;
  // Cursor surrounding
  const lines = content.split('\n');
  const halfWindow = Math.floor(config.cursorWindowLines / 2);
  const start = Math.max(0, cursorLine - halfWindow);
  const end = Math.min(lines.length, cursorLine + halfWindow);
  total += estimateTokens(lines.slice(start, end).join('\n'));

  if (config.includeImports) {
    const importLines = lines.filter(l => l.trim().startsWith('import ') || /require\(/.test(l));
    total += estimateTokens(importLines.join('\n'));
  }
  if (config.includeSymbols) {
    total += Math.ceil(lines.length * 0.15) * 8; // rough: ~15% of lines are signatures, ~8 tokens each
  }
  return Math.min(total, config.maxTokens);
}

/** Summary of the context window. */
export function contextSummary(window: ContextWindow): {
  items: number;
  tokens: number;
  utilization: number;
  types: string[];
} {
  const types = [...new Set(window.items.map(i => i.type))];
  return {
    items: window.items.length,
    tokens: window.totalTokens,
    utilization: window.utilization,
    types,
  };
}

// ─── Main assembly function ─────────────────────────────────────────────────

/**
 * THE main context assembly function. Gathers all context items from the
 * request and editor content, ranks by relevance, packs into token budget.
 */
export function assembleContext(
  request: AIRequest,
  editorContent: string,
  config: ContextConfig,
): ContextWindow {
  const budget = config.maxTokens - config.reserveForOutput;
  const items: ContextItem[] = [];

  // 1. Cursor surrounding (highest priority)
  if (request.context.cursorPosition) {
    items.push(
      extractCursorContext(editorContent, request.context.cursorPosition.line, config.cursorWindowLines),
    );
  }

  // 2. Selection
  if (request.context.selection) {
    items.push(extractSelectionContext(editorContent, request.context.selection));
  }

  // 3. Imports
  if (config.includeImports) {
    const importCtx = extractImportContext(editorContent);
    if (importCtx.content.length > 0) {
      items.push(importCtx);
    }
  }

  // 4. Symbols
  if (config.includeSymbols) {
    const symbolCtx = extractSymbolContext(editorContent);
    if (symbolCtx.content.length > 0) {
      items.push(symbolCtx);
    }
  }

  // 5. Symbol list from request context (lightweight)
  if (request.context.symbols && request.context.symbols.length > 0) {
    const symbolList = request.context.symbols.join(', ');
    const tokens = estimateTokens(symbolList);
    items.push({
      type: 'symbols',
      content: `Available symbols: ${symbolList}`,
      relevance: 0.5,
      tokens,
      source: 'request.context.symbols',
      priority: 4,
    });
  }

  // 6. Import list from request context
  if (request.context.imports && request.context.imports.length > 0) {
    const importList = request.context.imports.join('\n');
    const tokens = estimateTokens(importList);
    items.push({
      type: 'imports',
      content: importList,
      relevance: 0.5,
      tokens,
      source: 'request.context.imports',
      priority: 4,
    });
  }

  // 7. Language hint
  if (request.context.language) {
    items.push({
      type: 'instructions',
      content: `Language: ${request.context.language}`,
      relevance: 0.4,
      tokens: 4,
      source: 'request.context.language',
      priority: 3,
    });
  }

  // Rank by relevance to the request type, then pack into budget
  const ranked = rankByRelevance(items, request);
  return packContext(ranked, budget);
}
